import React, { useState } from 'react';
import SubTenantItem from './SubTenantItem';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../../auth/firebaseConfig';
import { useAuth } from '../../../auth/firebaseAuth';
import toast from 'react-hot-toast';

export interface SubTenant {
  id?: string;
  name: string;
  email?: string;
  phone?: string;
}

interface SubTenantsListProps {
  subTenants: SubTenant[];
  isActive?: boolean;
  tenancyId?: string;
  propertyId?: number | string;
  onRefresh?: () => void;
}

const SubTenantsList: React.FC<SubTenantsListProps> = (props) => {
  const {
    subTenants,
    isActive = true,
    tenancyId,
    propertyId,
    onRefresh
  } = props;
  const { currentUser } = useAuth();
  const [loadingId, setLoadingId] = useState<string | undefined>(undefined);
  const [confirmId, setConfirmId] = useState<string | undefined>(undefined);
  const [localSubTenants, setLocalSubTenants] = useState<SubTenant[]>(
    subTenants ?? []
  );

  React.useEffect(() => {
    setLocalSubTenants(subTenants ?? []);
  }, [subTenants]);

  // Remove confirmation handled via modal; no console logging in production.

  const handleRemove = async (id?: string) => {
    if (!id) return;

    try {
      setLoadingId(id);
      const token = await currentUser?.getIdToken?.();
      const resp = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/removeSubTenant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ tenancyId, propertyId, subTenantId: id })
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to remove sub-tenant');
      }

      toast.success('Sub-tenant removed');
      // Optimistically update local list so UI reflects removal immediately.
      setLocalSubTenants((prev) => prev.filter((st) => st.id !== id));
      onRefresh?.();
    } catch (e: any) {
      toast.error(e.message || 'Error removing sub-tenant');
    } finally {
      setLoadingId(undefined);
      setConfirmId(undefined);
    }
  };

  if (!localSubTenants || localSubTenants.length === 0) {
    return <p className="text-sm text-gray-500">No sub-tenants added.</p>;
  }

  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      <div className="flex items-center gap-2 mb-3">
        <h4
          className={`text-xs font-semibold uppercase tracking-wider ${
            isActive ? 'text-gray-700' : 'text-gray-500'
          }`}
        >
          Sub-tenants
        </h4>
        <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          {subTenants.length}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {localSubTenants.map((st, index) => (
          <SubTenantItem
            key={st.id ?? `sub-${index}`}
            id={st.id}
            name={st.name}
            email={st.email}
            phone={st.phone}
            index={index}
            onRemove={
              isActive && tenancyId && propertyId
                ? () => setConfirmId(st.id)
                : undefined
            }
          />
        ))}
      </div>

      {confirmId && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-remove-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
        >
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-2xl">
            <h2
              id="confirm-remove-title"
              className="text-base font-semibold text-gray-900"
            >
              Remove sub-tenant?
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              This action cannot be undone. The sub-tenant will be removed from
              this tenancy.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                onClick={() => setConfirmId(undefined)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                onClick={() => handleRemove(confirmId)}
                disabled={loadingId === confirmId}
              >
                {loadingId === confirmId ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubTenantsList;
// Removed duplicate, invalid component block below to fix TS parse errors
